import React, { FC, useRef, useEffect } from 'react';

// Utils
import { classNames } from '@sportal365/smp-widget-utils';

interface Props {
  selectedPillTab: string;
  pillTabOptions: { label: string; value: string; isDisabled?: boolean }[];
  handlePillTabChange: (value: string) => void;
}

export const PillTabsV2: FC<Props> = ({ selectedPillTab, pillTabOptions, handlePillTabChange }) => {
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);
  const pillsContainerRef = useRef<HTMLDivElement | null>(null);
  const selectedPillRef = useRef<HTMLDivElement | null>(null);

  const handleTabScroll = (value: string) => {
    handlePillTabChange(value);
    scrollToSelectedPill();
  };

  const scrollToSelectedPill = () => {
    if (scrollableContainerRef.current && pillsContainerRef.current && selectedPillRef.current) {
      const container = scrollableContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const pillRect = selectedPillRef.current.getBoundingClientRect();

      if (pillRect.right > containerRect.right || pillRect.left < containerRect.left) {
        if (pillRect.right > containerRect.right) {
          container.scrollLeft += pillRect.right - containerRect.right;
        } else {
          container.scrollLeft -= containerRect.left - pillRect.left;
        }
      }
    }
  };

  useEffect(() => {
    scrollToSelectedPill();
  }, [selectedPillTab]);

  return (
    <div
      ref={scrollableContainerRef}
      className='pill-tabs-scrollable-container flex gap-3 sm:gap-2 relative overflow-x-auto flex-[1_1_auto]'
      style={{
        scrollbarWidth: 'none',
      }}
    >
      <div ref={pillsContainerRef} className='pill-tabs-container flex gap-2 whitespace-nowrap'>
        {pillTabOptions.map(({ label, value, isDisabled }) => {
          const isSelected = value === selectedPillTab;

          if (!value) return null;

          return (
            <div
              key={value}
              ref={isSelected ? selectedPillRef : null}
              onClick={() => handleTabScroll(value)}
              className={classNames(
                {
                  'flex items-center justify-center sm:py-[10px] sm:px-6 py-[5px] px-3 h-fit cursor-pointer rounded-5 border-[1px]': true,
                  'bg-pillActiveBackgroundColor border-pillActiveBackgroundColor ': isSelected,
                  'bg-pillBackgroundColor border-pillBorderColor': !isSelected,
                  'pointer-events-none opacity-50': isDisabled && !isSelected,
                },
                `pill-tab-${label}`,
              )}
            >
              <span
                className={classNames(
                  {
                    'leading-[20px] text-3 sm:text-4': true,
                    'font-bold text-pillActiveTextColor': isSelected,
                    'font-normal text-pillTextColor': !isSelected,
                  },
                  `pill-tab-${label}-label`,
                )}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
